import React, { useReducer, useEffect, useContext } from "react";
import moduleConfig from "./moduleConfig";
import { Card, Row, Col } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import ModuleHeader from "../../UI/modules/views/partials/common/ModuleHeader";
import {
  createReducer,
  createInitialState,
} from "../../../reducers/module/createReducer";
import { BreadcrumbContext } from "../../../contexts/BreadcrumbContext";
import DashboardLayout from "../../UI/layouts/DashboardLayout";
import FormErrorMessage from "../../UI/errorMessages/FormErrorMessage";
import CMSFormInput from "../../UI/forms/CMSFormInput";
import BlockErrorMessage from "../../UI/errorMessages/BlockErrorMessage";
import CMSFormSelect from "../../UI/forms/CMSFormSelect";
import CMSFormRichText from "../../UI/forms/CMSFormRichText";
import CMSFormImage from "../../UI/forms/CMSFormImage";
import {
  loadDataForCreateItemGeneral,
  onSubmitGeneral,
} from "../../../helpers/ModuleHelperFunctions";
import CMSFormColor from "../../UI/forms/CMSFormColor";
import CMSFormMultiSelect from "../../UI/forms/CMSFormMultiSelect";
import CMSFormImageWithCrop from "../../UI/forms/CMSFormImageWithCrop";

const ProductCreate = () => {
  const [createState, createDispatch] = useReducer(
    createReducer,
    createInitialState
  );
  const { setBreadcrumb } = useContext(BreadcrumbContext);
  let navigate = useNavigate();

  const loadDataForCreateItem = async () => {
    await loadDataForCreateItemGeneral(
      `${moduleConfig.url}/fetch-data-for-create-item`,
      createDispatch
    );
  };

  useEffect(() => {
    loadDataForCreateItem();
    setBreadcrumb([
      moduleConfig.breadcrumbs.listing,
      moduleConfig.breadcrumbs.create,
    ]);
  }, []);
  let imageRequirement = {
    dimension: {
      width: 500,
      height: 500,
    },
    allowedExtensions: ["image/png", "image/jpg", "image/jpeg"],
    size: 1024 * 1024 * 3,
  };
  return (
    <DashboardLayout>
      {createState.status === "fetchFailed" ? (
        <BlockErrorMessage error={createState.error} />
      ) : (
        <>
          {/* {console.log(createState.data)} */}
          {createState.status !== "fetching" && (
            <>
              <ModuleHeader
                moduleTitle={`Create ${moduleConfig.singleItemTitle}`}
                moduleUrl={moduleConfig.url}
              ></ModuleHeader>

              <section className="content">
                <Card>
                  <Card.Body>
                    {createState.isLoading && (
                      <div className="overlay position-absolute containerLoading">
                        <i className="fas fa-2x fa-sync-alt fa-spin"></i>
                      </div>
                    )}
                    <Formik
                      initialValues={{
                        title: "",
                        categories: [],
                        materials: [],
                        brand: "",
                        file: "",
                        price: "",
                        salePrice: "",
                        description: "",
                        amazonLink: "",
                        noonLink: "",
                        inStock: "",

                        status: "0",
                        showOnHomePage: "0",
                      }}
                      validationSchema={yup.object({
                        title: yup
                          .string()
                          .required("Title is required")
                          .max(
                            100,
                            "Title should contain at most 100 characters"
                          ),

                        categories: yup
                          .array()
                          .of(yup.string())
                          .min(1, "Please select at least one category"),

                        materials: yup
                          .array()
                          .of(yup.string())
                          .nullable()
                          .min(1, "Please select at least one material"),

                        description: yup
                          .string()
                          .required("Description is required"),

                        file: yup
                          .mixed()
                          .required("Please upload an image")
                          .imageTypeCheck(
                            moduleConfig.imageRequirement.allowedExtensions
                          )
                          .imageSizeCheck(moduleConfig.imageRequirement.size),

                        brand: yup.string().nullable(),

                        price: yup
                          .number()
                          .required("Price is required")
                          .transform((value, originalValue) =>
                            originalValue === "" ? null : value
                          )
                          .nullable()
                          .typeError("Price must be a valid number"),

                        salePrice: yup
                          .number()
                          .required("Sale price is required")
                          .typeError("Sale price must be a valid number"),

                        amazonLink: yup
                          .string()
                          .url("Please enter a valid Amazon URL")
                          .nullable(),

                        noonLink: yup
                          .string()
                          .url("Please enter a valid Noon URL")
                          .nullable(),

                        inStock: yup
                          .string()
                          .required("Stock status is required"),

                        status: yup.string().required("Status is required"),
                        showOnHomePage: yup.string().required("Show on home page is required"),
                      })}
                      enableReinitialize={true}
                      validateOnChange={true}
                      validateOnBlur={true}
                      onSubmit={async (values, { resetForm }) => {
                        const formData = new FormData();
                        for (let name in values) {
                          if (name === "categories" || name === "materials") {
                            if (Array.isArray(values[name])) {
                              for (var i = 0; i < values[name].length; i++) {
                                formData.append(name + "[]", values[name][i]);
                              }
                            }
                          } else if (Array.isArray(values[name])) {
                            formData.append(name, JSON.stringify(values[name]));
                          } else {
                            formData.append(name, values[name]);
                          }
                        }
                        await onSubmitGeneral(
                          formData,
                          moduleConfig.url,
                          moduleConfig.url,
                          createDispatch,
                          navigate
                        );
                      }}
                    >
                      {(form) => (
                        <Form onSubmit={form.handleSubmit}>
                          {createState.error && (
                            <FormErrorMessage error={createState.error} />
                          )}

                          {/* {console.log(form.errors)} */}
                          <CMSFormInput
                            name="title"
                            label="Title"
                            required={true}
                            form={form}
                          />

                          <CMSFormMultiSelect
                            name="categories"
                            label="Categories"
                            selectionValue="value"
                            selected={form.values.categories}
                            required={true}
                            form={form}
                            options={
                              createState.data && createState.data.categories
                                ? createState.data.categories
                                : []
                            }
                            displayValue="label"
                          />

                          <CMSFormMultiSelect
                            name="materials"
                            label="Materials"
                            selectionValue="value"
                            selected={form.values.materials}
                            required={true}
                            form={form}
                            options={
                              createState.data && createState.data.materials
                                ? createState.data.materials
                                : []
                            }
                            displayValue="label"
                          />

                          <CMSFormInput
                            name="brand"
                            label="Brand"
                            required={false}
                            form={form}
                          />

                          <CMSFormInput
                            name="price"
                            label="Price"
                            info="Add the sale price when there is no discount; if a discount exists, apply the original price instead."
                            form={form}
                            required={true}
                          />

                          <CMSFormInput
                            name="salePrice"
                            label="Sale Price"
                            info=""
                            form={form}
                            required={true}
                          />

                          <CMSFormImageWithCrop
                            label="Image"
                            name="file"
                            info="Recommended dimensions 500*500"
                            form={form}
                            required={true}
                            aspect={1 / 1}
                            outputSize={imageRequirement.dimension}
                          />

                          <CMSFormRichText
                            name="description"
                            label="Description"
                            form={form}
                            required={true}
                          />

                          <CMSFormInput
                            name="amazonLink"
                            label="Amazon Link"
                            required={false}
                            form={form}
                          />

                          <CMSFormInput
                            name="noonLink"
                            label="Noon Link"
                            required={false}
                            form={form}
                          />

                          <CMSFormSelect
                            name="inStock"
                            label="In-Stock"
                            required={true}
                            form={form}
                            options={[
                              { label: "Yes", value: "1" },
                              { label: "No", value: "0" },
                            ]}
                          />

                          <CMSFormSelect
                            name="showOnHomePage"
                            label="Show On Home Page?"
                            required={true}
                            form={form}
                            options={[
                              { label: "Yes", value: "1" },
                              { label: "No", value: "0" },
                            ]}
                          />

                          <CMSFormSelect
                            name="status"
                            label="Status"
                            required={true}
                            form={form}
                            options={[
                              { label: "Select Status...", value: "" },
                              { label: "Enabled", value: "1" },
                              { label: "Disabled", value: "0" },
                            ]}
                          />

                          <button
                            type="submit"
                            onClick={() => form.handleSubmit}
                            className="btn btn-primary"
                          >
                            Submit
                          </button>
                        </Form>
                      )}
                    </Formik>
                  </Card.Body>
                </Card>
              </section>
            </>
          )}
        </>
      )}
    </DashboardLayout>
  );
};

export default ProductCreate;
